/**
 * Super-Admin — Monatlicher KI-Modell-Bericht: Modelle prüfen, Stärken & Mehrkosten vs. aktives Modell, Mail an Super-Admins.
 */
import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { apiClient } from "@/lib/apiClient";
import { toast } from "sonner";
import { CalendarClock, Loader2, Mail, FileText, ExternalLink } from "lucide-react";
import { formatDateTime } from "@/lib/formatDate";

const chf = (usd, rate) => (usd === null || usd === undefined ? "–" : `CHF ${(usd * rate).toFixed(2)}`);

function CandidateRow({ c, rate }) {
  const delta = c.delta_usd;
  const cls = delta === null ? "text-slate-500" : delta > 0.005 ? "text-rose-700" : delta < -0.005 ? "text-emerald-700" : "text-slate-600";
  const txt = delta === null ? "Preis unbekannt" : Math.abs(delta) < 0.005 ? "gleiche Kosten" : `${delta > 0 ? "+" : "−"}${chf(Math.abs(delta), rate)}/Monat`;
  return (
    <tr className="border-t border-slate-100" data-testid={`model-report-candidate-${c.model}`}>
      <td className="py-1.5 pr-3 font-mono text-xs text-slate-800">{c.model}</td>
      <td className="py-1.5 pr-3 text-xs text-slate-600 whitespace-nowrap">{c.price ? `$${c.price[0]} / $${c.price[1]}` : "?"}</td>
      <td className="py-1.5 pr-3 text-xs text-slate-700">{c.strengths}</td>
      <td className={`py-1.5 text-xs font-medium whitespace-nowrap ${cls}`}>{txt}</td>
    </tr>
  );
}

export default function AiModelReportCard() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [showText, setShowText] = useState(false);

  const load = async () => {
    try {
      const r = await apiClient.get("/admin/ai-providers/model-report");
      setReport(r.data?.id ? r.data : null);
    } catch { /* ignore */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const run = async (sendEmail) => {
    setRunning(true);
    try {
      const r = await apiClient.post(`/admin/ai-providers/model-report?send_email=${sendEmail}`);
      setReport(r.data);
      if (sendEmail) {
        if (r.data.email_sent) toast.success("Bericht erstellt und per E-Mail an alle Super-Admins gesendet");
        else toast.warning(`Bericht erstellt – E-Mail nicht gesendet: ${r.data.email_error || "SMTP prüfen"}`);
      } else toast.success("Bericht erstellt");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bericht fehlgeschlagen");
    } finally { setRunning(false); }
  };

  return (
    <Card className="p-5 border-slate-200" data-testid="ai-model-report-card">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2">
            <CalendarClock className="h-5 w-5 text-violet-700" />
            <span className="text-lg font-semibold text-slate-900">Monatlicher Modell-Bericht</span>
            <Badge variant="outline" data-testid="model-report-schedule-badge">automatisch am 1. des Monats, 06:00</Badge>
          </div>
          <p className="text-sm text-slate-500 mt-1">
            Prüft alle Modelle, vergleicht jedes neuere Modell mit dem aktiven: was es besser macht und was es bei deinem Volumen mehr oder weniger kostet. Vollständiger Bericht per E-Mail an alle Super-Admins.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => run(false)} disabled={running} data-testid="model-report-run-btn">
            {running ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <FileText className="h-4 w-4 mr-1" />} Jetzt erstellen
          </Button>
          <Button size="sm" onClick={() => run(true)} disabled={running} data-testid="model-report-email-btn">
            {running ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Mail className="h-4 w-4 mr-1" />} Erstellen &amp; mailen
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-slate-400 py-4">Lade …</div>
      ) : !report ? (
        <div className="text-sm text-slate-500 py-4" data-testid="model-report-empty">Noch kein Bericht erstellt.</div>
      ) : (
        <div className="space-y-4" data-testid="model-report-content">
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
            <span>Erstellt {formatDateTime(report.created_at)}</span>
            <span>Kostenbasis: {report.volume_label || report.month}</span>
            <span className={report.email_sent ? "text-emerald-700" : "text-amber-700"} data-testid="model-report-email-status">
              {report.email_sent ? "E-Mail gesendet" : `E-Mail: ${report.email_error || "nicht gesendet"}`}
            </span>
          </div>
          {report.providers.map((p) => (
            <div key={p.provider} className="rounded-lg border border-slate-200 p-3" data-testid={`model-report-provider-${p.provider}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="font-medium text-slate-900">{p.label}</div>
                <a href={p.pricing_url} target="_blank" rel="noopener noreferrer" className="text-xs text-indigo-700 underline inline-flex items-center gap-1">Preisliste <ExternalLink className="h-3 w-3" /></a>
              </div>
              <div className="text-xs text-slate-700 mt-1">
                Aktiv: <span className="font-mono">{p.active_model}</span> · {p.active_price_line} · Kontext {p.active_context} · Kosten {chf(p.active_cost_usd, report.usd_to_chf)}/Monat
                {p.volume?.reference && <span className="text-slate-500"> (Referenzvolumen 2M/0.5M Tokens – noch kein Verbrauch protokolliert)</span>}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">{p.active_strengths}</div>
              {p.candidates.length > 0 ? (
                <table className="w-full mt-2">
                  <thead><tr className="text-[11px] text-slate-500 text-left"><th className="pr-3 font-medium">Alternative</th><th className="pr-3 font-medium">$ Ein/Aus pro 1M</th><th className="pr-3 font-medium">Was es besser/anders macht</th><th className="font-medium">Kosten vs. aktiv</th></tr></thead>
                  <tbody>{p.candidates.map((c) => <CandidateRow key={c.model} c={c} rate={report.usd_to_chf} />)}</tbody>
                </table>
              ) : <div className="text-xs text-slate-500 mt-2">Keine neueren Kandidaten gefunden.</div>}
            </div>
          ))}
          <button type="button" className="text-xs text-indigo-700 underline" onClick={() => setShowText(!showText)} data-testid="model-report-toggle-text">
            {showText ? "E-Mail-Text ausblenden" : "E-Mail-Text anzeigen"}
          </button>
          {showText && <pre className="text-[11px] whitespace-pre-wrap bg-slate-50 border border-slate-200 rounded p-3 text-slate-700" data-testid="model-report-text">{report.text}</pre>}
        </div>
      )}
    </Card>
  );
}
